import React, { useMemo, useState } from "react"
import { Text, Tag } from "@chakra-ui/react"
import Link from "@hydrologiq/shared/components/Link"
import StatusBadge, { StatusType } from "@hydrologiq/shared/components/StatusBadge"
import { ColumnDef } from "@tanstack/react-table"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { toUserTZ } from "@hydrologiq/common/helpers/date"
import { DateTime } from "timezonecomplete"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { PaginatedTable, ServerPagination, ServerFiltering } from "@hydrologiq/shared/components/PaginatedTable"
import { SortConfig } from "@hydrologiq/shared/types/pagination"
import { MultiAssetDisplay } from "@hydrologiq/shared/components/activities"
import { useIsSectionEnabled } from "@hydrologiq/shared/providers/SectionFeatures"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"
import OrderEdit from "./OrderEdit"
import OrdersExport from "./OrdersExport"
import ExpandedOrderFulfilmentsContainer from "./ExpandedOrderFulfilmentsContainer"

// Format date as "MMM DD, YYYY"
function formatOrderDate(dateTime: DateTime): string {
  return dateTime.format("MMM dd, yyyy")
}

export interface OrderFilters {
  [key: string]: string | undefined
  status?: string
  type?: string
  customer?: string
  site?: string
  asset?: string
  name?: string
  latest_event_status?: string
}

interface OrdersTableAdvancedProps {
  orders: gqlTypes.Order[]
  loading?: boolean
  refreshing?: boolean
  error?: string | null
  onRetry?: () => void
  serverPagination?: ServerPagination
  serverFiltering?: ServerFiltering<OrderFilters> & {
    defaultFilters?: OrderFilters
    defaultSort?: SortConfig | null
  }
  headerActions?: React.ReactNode
  actionsRight?: React.ReactNode
  filterOptions?: gqlTypes.OrderFilterOptions
}

function OrdersTableAdvanced({
  orders,
  loading,
  error,
  onRetry,
  serverPagination,
  serverFiltering,
  headerActions,
  filterOptions,
}: OrdersTableAdvancedProps) {
  const isCustomersEnabled = useIsSectionEnabled("customers")
  const activityless = useGetFeature("orders_activityless")

  // Controlled expansion state so OrderEdit can expand rows
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string>>(new Set())

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER],
    [COPY_KEYS.HEADINGS.NAME]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.NAME],
    [COPY_KEYS.HEADINGS.CUSTOMER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.CUSTOMER],
    [COPY_KEYS.HEADINGS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.SITE],
    [COPY_KEYS.HEADINGS.ASSETS]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ASSETS],
    [COPY_KEYS.HEADINGS.STATUS]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.STATUS],
    [COPY_KEYS.HEADINGS.START_DATE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.START_DATE],
    [COPY_KEYS.HEADINGS.END_DATE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.END_DATE],
    [COPY_KEYS.HEADINGS.ACTIVITIES]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ACTIVITIES],
    [COPY_KEYS.HEADINGS.QUANTITY_FULFILLED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.QUANTITY_FULFILLED],
    [COPY_KEYS.HEADINGS.QUANTITY_CONSUMED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.QUANTITY_CONSUMED],
    [COPY_KEYS.HEADINGS.QUANTITY_ORDERED]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.QUANTITY_ORDERED],
  })

  // Build filter options for each column from server-provided OrderFilterOptions
  const nameFilterOptions = useMemo(
    () => filterOptions?.orders?.map((o) => ({ label: o.name, value: o.value })),
    [filterOptions?.orders]
  )
  const customerFilterOptions = useMemo(
    () => filterOptions?.customers?.map((o) => ({ label: o.name, value: o.value })),
    [filterOptions?.customers]
  )
  const siteFilterOptions = useMemo(
    () => filterOptions?.sites?.map((o) => ({ label: o.name, value: o.value })),
    [filterOptions?.sites]
  )
  const assetFilterOptions = useMemo(
    () => filterOptions?.assets?.map((o) => ({ label: o.name, value: o.value })),
    [filterOptions?.assets]
  )
  const statusFilterOptions = useMemo(
    () => filterOptions?.statuses?.map((o) => ({ label: o.name, value: o.value })),
    [filterOptions?.statuses]
  )
  const typeFilterOptions = useMemo(
    () => filterOptions?.types?.map((o) => ({ label: o.name, value: o.value })),
    [filterOptions?.types]
  )

  // Define table columns
  const columns = useMemo<ColumnDef<gqlTypes.Order, any>[]>(
    () => {
      const allColumns: ColumnDef<gqlTypes.Order, any>[] = [
      {
        accessorKey: "name",
        header: `${copy[COPY_KEYS.HEADINGS.ORDER]} ID`,
        cell: ({ row }) => {
          const order = row.original
          return (
            <OrderEdit
              enableAddActivity={false}
              order={order}
              onActivityCreated={() => {
                setExpandedOrderIds((prev) => new Set(prev).add(order.id))
              }}
              onManageActivities={() => {
                setExpandedOrderIds((prev) => new Set(prev).add(order.id))
              }}
            />
          )
        },
        meta: {
          filterVariant: "select" as const,
          filterOptions: nameFilterOptions,
        },
        size: 200,
        minSize: 160,
      },
      {
        id: "customer",
        accessorFn: (row) => row.sites?.[0]?.customer?.id || null,
        header: copy[COPY_KEYS.HEADINGS.CUSTOMER],
        cell: ({ row }) => {
          const customer = row.original.sites?.[0]?.customer
          return customer ? (
            <Link variant="primary" href={`/customers/${customer.id}`} target="_blank" deLink={!isCustomersEnabled}>
              {customer.name}
            </Link>
          ) : null
        },
        meta: {
          filterVariant: "select" as const,
          filterOptions: customerFilterOptions,
          filterFormatter: (value: any) => {
            if (!value) return "All"
            const order = orders.find((o) => o.sites?.[0]?.customer?.id === value)
            return order?.sites?.[0]?.customer?.name || value
          },
        },
      },
      {
        accessorKey: "status",
        header: copy[COPY_KEYS.HEADINGS.STATUS],
        cell: ({ getValue }) => {
          const status = getValue() as string
          const mapOrderStatusToBadge = (orderStatus: string): StatusType => {
            const lowerStatus = orderStatus.toLowerCase()
            if (lowerStatus === "started") return "in_progress"
            if (lowerStatus === "completed") return "completed"
            if (lowerStatus === "billed") return "billed"
            return "scheduled"
          }
          return <StatusBadge status={mapOrderStatusToBadge(status)} />
        },
        meta: {
          filterVariant: "select" as const,
          filterOptions: statusFilterOptions,
        },
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ getValue }) => {
          const type = getValue() as string
          const typeUpper = type?.toUpperCase()
          return (
            <Tag.Root colorPalette={typeUpper === "VMI" ? "purple" : "orange"}>
              <Tag.Label>{typeUpper}</Tag.Label>
            </Tag.Root>
          )
        },
        meta: {
          filterVariant: "select" as const,
          filterOptions: typeFilterOptions,
        },
      },
      {
        accessorKey: "start",
        header: copy[COPY_KEYS.HEADINGS.START_DATE],
        cell: ({ getValue }) => <Text>{formatOrderDate(toUserTZ(getValue() as string))}</Text>,
        enableColumnFilter: false,
      },
      {
        accessorKey: "end",
        header: copy[COPY_KEYS.HEADINGS.END_DATE],
        cell: ({ getValue }) => <Text>{getValue() ? formatOrderDate(toUserTZ(getValue() as string)) : "n/a"}</Text>,
        enableColumnFilter: false,
      },
      {
        id: "site",
        accessorFn: (row) => row.sites?.[0]?.site?.id || null,
        header: copy[COPY_KEYS.HEADINGS.SITE],
        cell: ({ row }) => {
          const site = row.original.sites?.[0]?.site
          const customer = row.original.sites?.[0]?.customer
          return site && customer ? (
            <Link variant="primary" href={`/customers/${customer.id}/sites/${site.id}`} deLink={!isCustomersEnabled}>
              {site.name}
            </Link>
          ) : null
        },
        meta: {
          filterVariant: "select" as const,
          filterOptions: siteFilterOptions,
          filterFormatter: (value: any) => {
            if (!value) return "All"
            const order = orders.find((o) => o.sites?.[0]?.site?.id === value)
            return order?.sites?.[0]?.site?.name || value
          },
        },
      },
      {
        id: "asset",
        accessorFn: (row) => row.assets?.[0]?.id || null,
        header: copy[COPY_KEYS.HEADINGS.ASSETS],
        cell: ({ row }) => {
          const assets = row.original.assets || []
          return <MultiAssetDisplay assets={assets} />
        },
        meta: {
          filterVariant: "select" as const,
          filterOptions: assetFilterOptions,
          filterFormatter: (value: any) => {
            if (!value) return "All"
            const order = orders.find((o) => o.assets?.some((asset) => asset.id === value))
            const asset = order?.assets?.find((a) => a.id === value)
            return asset?.name || value
          },
        },
      },
      {
        accessorKey: "quantity",
        header: copy[COPY_KEYS.HEADINGS.QUANTITY_ORDERED],
        cell: ({ getValue }) => <Text>{getValue() || "n/a"}</Text>,
        enableColumnFilter: false,
      },
      {
        accessorKey: "quantity_fulfilled",
        header: copy[COPY_KEYS.HEADINGS.QUANTITY_FULFILLED],
        cell: ({ getValue }) => <Text>{getValue() || "n/a"}</Text>,
        enableColumnFilter: false,
        meta: { activityDerived: true },
      },
      {
        id: "quantity_collected",
        accessorFn: (row) => (row as any).quantity_collected,
        header: "Collected (kg)",
        cell: ({ getValue }) => {
          const value = getValue()
          return <Text>{value != null ? value : "—"}</Text>
        },
        enableColumnFilter: false,
      },
      {
        accessorKey: "quantity_consumed",
        header: copy[COPY_KEYS.HEADINGS.QUANTITY_CONSUMED],
        cell: ({ getValue }) => {
          const value = getValue()
          return <Text>{value !== null && value !== undefined ? value : "n/a"}</Text>
        },
        enableColumnFilter: false,
      },
    ]

      if (activityless) {
        return allColumns.filter((col) => !(col as any).meta?.activityDerived)
      }
      return allColumns
    },
    [
      orders,
      copy,
      isCustomersEnabled,
      activityless,
      nameFilterOptions,
      customerFilterOptions,
      statusFilterOptions,
      typeFilterOptions,
      siteFilterOptions,
      assetFilterOptions,
    ]
  )

  return (
    <PaginatedTable<gqlTypes.Order, OrderFilters>
      data={orders}
      columns={columns}
      loading={loading}
      error={error}
      onRetry={onRetry}
      serverPagination={serverPagination}
      serverFiltering={serverFiltering}
      headerActions={headerActions}
      entityName="orders"
      exportComponent={
        serverFiltering ? (
          <OrdersExport filters={serverFiltering.filters} sort={serverFiltering.sort} />
        ) : undefined
      }
      expandableRows={activityless ? undefined : {
        renderExpandedContent: (order: gqlTypes.Order) => (
          <ExpandedOrderFulfilmentsContainer orderId={order.id} order={order} />
        ),
        getRowId: (order: gqlTypes.Order) => order.id,
        expandedRowIds: expandedOrderIds,
        onExpandedChange: setExpandedOrderIds,
      }}
    />
  )
}

export default OrdersTableAdvanced
